<script>
  import { tick } from 'svelte'

  let { title, onSave, onSaved = () => {}, testId = 'editable-title', class: className = '', slashCommands = [], dblClickToEdit = false } = $props()
  let el = $state(null)
  let editing = $state(false)
  let debounceTimer = null
  let lastSavedText = title
  let editText = $state('')
  let autocompleteDismissed = $state(false)
  let autocompleteIndex = $state(0)

  $effect(() => {
    if (!editing) {
      lastSavedText = title
    }
  })

  let autocompleteMatches = $derived.by(() => {
    if (slashCommands.length === 0) return []
    const match = editText.match(/(?:^|\s)(\/\S*)$/)
    if (!match) return []
    const partial = match[1].toLowerCase()
    return slashCommands.filter(c => c.command.startsWith(partial) && c.command !== partial)
  })

  let autocompleteVisible = $derived(!autocompleteDismissed && autocompleteMatches.length > 0)

  function renderMarkdown(text) {
    if (!text) return ''
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
        if (/^https?:\/\//.test(url)) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-node underline">${label}</a>`
        }
        return `[${label}](${url})`
      })
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/(?<!["\w/])(?:https?:\/\/[^\s<]+|www\.[^\s<]+\.[a-z]{2,}[^\s<]*)/gi, (url) => {
        const href = url.startsWith('www.') ? `https://${url}` : url
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-node underline">${url}</a>`
      })
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>')
  }

  async function startEditing(event) {
    if (event.target.closest('a')) return
    editing = true
    await tick()
    if (!el) return
    el.innerText = title
    editText = title
    autocompleteDismissed = false
    el.focus()
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  function scheduleAutoSave() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => autoSave(), 1500)
  }

  async function autoSave() {
    if (!el) return
    const trimmed = el.innerText.trim()
    if (!trimmed || trimmed === lastSavedText) return

    lastSavedText = trimmed
    await onSave(trimmed)
    onSaved()
  }

  async function save() {
    clearTimeout(debounceTimer)
    if (!editing) return
    editing = false

    const trimmed = el.innerText.trim()
    if (!trimmed) return

    if (trimmed !== lastSavedText) {
      lastSavedText = trimmed
      await onSave(trimmed)
      onSaved()
    }
  }

  function handleInput() {
    editText = el?.innerText || ''
    autocompleteDismissed = false
    autocompleteIndex = 0
    scheduleAutoSave()
  }

  function applyAutocomplete(command) {
    if (!el) return
    el.innerText = el.innerText.replace(/(?:^|\s)(\/\S*)$/, (match, partial) => {
      const prefix = match.startsWith(' ') ? ' ' : ''
      return prefix + command + ' '
    })
    editText = el.innerText
    autocompleteDismissed = true
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
    el.focus()
  }

  $effect(() => {
    if (!el || !dblClickToEdit) return
    function onMouseDown() {
      if (!editing) return
      editing = false
      el.contentEditable = 'false'
      clearTimeout(debounceTimer)
      const trimmed = el.innerText.trim()
      if (trimmed && trimmed !== lastSavedText) {
        lastSavedText = trimmed
        onSave(trimmed)
        onSaved()
      }
    }
    el.addEventListener('mousedown', onMouseDown)
    return () => el.removeEventListener('mousedown', onMouseDown)
  })

  function handleKeydown(event) {
    if (autocompleteVisible) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        autocompleteIndex = (autocompleteIndex + 1) % autocompleteMatches.length
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        autocompleteIndex = (autocompleteIndex - 1 + autocompleteMatches.length) % autocompleteMatches.length
        return
      }
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault()
        applyAutocomplete(autocompleteMatches[autocompleteIndex].command)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        autocompleteDismissed = true
        return
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      el.blur()
    }
    if (event.key === 'Escape') {
      clearTimeout(debounceTimer)
      editing = false
      el.blur()
    }
  }
</script>

{#if editing}
  <span class="relative {className}">
    <span
      bind:this={el}
      data-testid="{testId}-input"
      contenteditable="true"
      role="textbox"
      onblur={save}
      oninput={handleInput}
      onkeydown={handleKeydown}
      class="block cursor-text outline-none"
      style="caret-color: var(--color-text);"
    ></span>
    {#if autocompleteVisible}
      <ul
        data-testid="slash-autocomplete"
        class="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-border bg-surface-1 py-1 text-sm shadow-lg shadow-black/20"
        style="min-width: 200px;"
      >
        {#each autocompleteMatches as match, i}
          <li>
            <button
              onmousedown={(e) => { e.preventDefault(); applyAutocomplete(match.command) }}
              class="w-full px-3 py-1.5 text-left cursor-pointer flex items-center gap-2 transition-colors"
              class:bg-surface-2={i === autocompleteIndex}
            >
              <span class="font-mono text-synapse">{match.command}</span>
              <span class="text-text-muted">{match.label}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </span>
{:else}
  <span
    data-testid={testId}
    onclick={dblClickToEdit ? undefined : startEditing}
    ondblclick={dblClickToEdit ? startEditing : undefined}
    class="{className} cursor-text"
  >{@html renderMarkdown(title)}</span>
{/if}
